import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowLeft, 
  Brain, 
  Activity, 
  Settings, 
  AlertCircle, 
  CheckCircle, 
  TrendingUp,
  Pause,
  Play,
  RefreshCw,
  Download,
  ChevronDown,
  Zap,
  FileText,
  Wrench,
  BookOpen,
  Shield,
  GitBranch
} from 'lucide-react';
import { fullAgentSuite, platformStatus } from '../../data/enhancedAgentsData';
import { Agent } from '../../types';

interface AgentTask {
  id: string;
  documentName: string;
  workflowId: string;
  status: 'processing' | 'queued' | 'completed' | 'failed';
  priority: 'high' | 'normal' | 'low';
  startTime?: Date;
  progress?: number;
  currentStep?: string;
}

interface AgentMetrics {
  last24Hours: {
    processed: number;
    failed: number;
    avgProcessingTime: number;
    peakHour: string;
  };
  lastHour: {
    processed: number;
    failed: number;
    queueDepth: number;
    avgWaitTime: number;
  };
  performance: {
    cpuUsage: number;
    memoryUsage: number;
    apiLatency: number;
    throughput: number;
  };
}

interface AgentConfig {
  version: string;
  lastUpdated: Date;
  validationRules: Record<string, any>;
  mlModels: Record<string, string>;
  thresholds: Record<string, number>;
  retryPolicy: {
    maxAttempts: number;
    backoffMultiplier: number;
    timeoutMs: number;
  };
}

// Enhanced agent dependencies with provider awareness
const agentDependencies = {
  'insurverify': {
    tools: ['SubmissionDataFetcher', 'ACORDFormValidator', 'DataQualityChecker'],
    ragAssets: ['InsuranceFieldMappings', 'ValidationRulesets', 'ACORDStandards'],
    provider: 'native'
  },
  'exposureinsights': {
    tools: ['SubmissionDataFetcher', 'RiskCalculator', 'GeospatialAnalyzer'],
    ragAssets: ['ExposureModels', 'CatastropheData', 'RiskFactorDatabase'],
    provider: 'native'
  },
  'fraud-detector': {
    tools: ['AWS Agent Runtime', 'Pattern Analyzer', 'Risk Scorer'],
    ragAssets: ['Fraud Patterns', 'Behavioral Models', 'Network Analysis'],
    provider: 'hosted'
  },
  'settlement-analyzer': {
    tools: ['Market Data API', 'Settlement Calculator', 'Benchmarking Engine'],
    ragAssets: ['Market Comparisons', 'Settlement History', 'Industry Benchmarks'],
    provider: 'hosted'
  },
};

export const AgentOrchestrator: React.FC = () => {
  const navigate = useNavigate();
  const { agentId } = useParams<{ agentId?: string }>();
  
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(
    agentId ? fullAgentSuite.find(a => a.id === agentId) || fullAgentSuite[0] : fullAgentSuite[0]
  );
  
  const [isAgentPaused, setIsAgentPaused] = useState(false);
  const [activeTab, setActiveTab] = useState<'performance' | 'configuration' | 'integration'>('performance');
  
  // Mock data for current tasks
  const [currentTasks, setCurrentTasks] = useState<AgentTask[]>([
    {
      id: 'TSK-8974',
      documentName: 'SUB0010006_ACORD125.pdf',
      workflowId: 'submission-intake',
      status: 'processing',
      priority: 'high',
      startTime: new Date(Date.now() - 2300),
      progress: 82,
      currentStep: 'Extracting policy limits...'
    },
    {
      id: 'TSK-8975',
      documentName: 'Workers_Comp_App.pdf',
      workflowId: 'submission-intake',
      status: 'queued',
      priority: 'high'
    },
    {
      id: 'TSK-8976',
      documentName: 'Fraud_Investigation.json',
      workflowId: 'claims-processing',
      status: 'queued',
      priority: 'normal'
    }
  ]);

  // Enhanced metrics with provider-specific data
  const [metrics, setMetrics] = useState<AgentMetrics>({
    last24Hours: {
      processed: selectedAgent?.provider === 'hosted' ? 1247 : 1847,
      failed: selectedAgent?.provider === 'hosted' ? 31 : 23,
      avgProcessingTime: selectedAgent?.provider === 'hosted' ? 52 : 45,
      peakHour: '14:00-15:00'
    },
    lastHour: {
      processed: selectedAgent?.provider === 'hosted' ? 38 : 47,
      failed: selectedAgent?.provider === 'hosted' ? 2 : 1,
      queueDepth: 4,
      avgWaitTime: selectedAgent?.provider === 'hosted' ? 1.8 : 1.2
    },
    performance: {
      cpuUsage: 42,
      memoryUsage: 68,
      apiLatency: selectedAgent?.provider === 'hosted' ? 284 : 234,
      throughput: selectedAgent?.provider === 'hosted' ? 0.65 : 0.78
    }
  });

  const [agentConfig] = useState<AgentConfig>({
    version: selectedAgent?.provenance?.version || '2.3.1',
    lastUpdated: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000),
    validationRules: {
      requiredFields: ['insuredName', 'effectiveDate', 'limits'],
      confidence_threshold: 0.85,
      data_quality_min: 0.75
    },
    mlModels: {
      primary_model: selectedAgent?.provider === 'hosted' ? 'aws-agent-v2.1' : 'elevate-v3.2',
      extraction_model: 'acord-extract-v2.1',
      validation_model: 'field-validate-v1.8'
    },
    thresholds: {
      confidence_threshold: 0.85,
      quality_threshold: 0.75,
      retry_threshold: 3,
      timeout_seconds: selectedAgent?.provider === 'hosted' ? 450 : 300
    },
    retryPolicy: {
      maxAttempts: 3,
      backoffMultiplier: 2,
      timeoutMs: selectedAgent?.provider === 'hosted' ? 450000 : 300000
    }
  });

  const getProviderInfo = (agent: Agent | null) => {
    if (!agent) return { color: 'var(--text-secondary)', icon: <Brain size={32} />, label: 'Standard' };
    
    switch (agent.provider) {
      case 'native':
        return {
          color: 'var(--provider-native)',
          icon: <Zap size={32} />,
          label: 'Native ElevateNow Agent',
          description: 'Certified insurance intelligence',
          benefits: ['Deep domain expertise', 'Optimized performance', 'Seamless integration']
        };
      case 'hosted':
        return {
          color: 'var(--provider-hosted)',
          icon: <GitBranch size={32} />,
          label: 'Hosted External Agent',
          description: 'AWS Agent Code integration',
          benefits: ['Multi-vendor support', 'Flexible deployment', 'Cross-platform coordination']
        };
      default:
        return {
          color: 'var(--text-secondary)',
          icon: <Brain size={32} />,
          label: 'Standard Agent'
        };
    }
  };

  const providerInfo = getProviderInfo(selectedAgent);

  // Simulate live updates
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTasks(prev => prev.map(task => {
        if (task.status === 'processing' && task.progress && task.progress < 100) {
          return { ...task, progress: Math.min(task.progress + Math.random() * 10, 100) };
        }
        return task;
      }));

      setMetrics(prev => ({
        ...prev,
        performance: {
          cpuUsage: Math.round(40 + Math.random() * 20),
          memoryUsage: Math.round(65 + Math.random() * 10),
          apiLatency: Math.round((selectedAgent?.provider === 'hosted' ? 250 : 200) + Math.random() * 100),
          throughput: parseFloat(((selectedAgent?.provider === 'hosted' ? 0.6 : 0.7) + Math.random() * 0.2).toFixed(2))
        }
      }));
    }, 2000);

    return () => clearInterval(interval);
  }, [selectedAgent]);

  const getCapabilityIcon = (capability: string) => {
    const icons: Record<string, JSX.Element> = {
      'Cross-System Data Validation': <CheckCircle size={14} />,
      'Multi-Vendor Coordination': <GitBranch size={14} />,
      'Behavioral Analysis': <Brain size={14} />,
      'Settlement Benchmarking': <TrendingUp size={14} />,
      'PDF Extraction': <FileText size={14} />,
      'Data Validation': <CheckCircle size={14} />,
      'Format Conversion': <RefreshCw size={14} />,
      'ACORD Form Recognition': <FileText size={14} />,
      'OCR Processing': <Brain size={14} />,
      'Risk Scoring': <TrendingUp size={14} />,
      'Compliance Check': <AlertCircle size={14} />
    };
    return icons[capability] || <Zap size={14} />;
  };

  // Get dependencies for selected agent
  const dependencies = selectedAgent ? agentDependencies[selectedAgent.id as keyof typeof agentDependencies] : null;

  return (
    <div style={{ padding: '20px', background: 'var(--divider-light)', minHeight: 'calc(100vh - 56px)' }}>
      {/* Enhanced Header */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
          <button 
            onClick={() => navigate('/')}
            className="sn-button"
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
            }}
          >
            <ArrowLeft size={16} />
            Back to Dashboard
          </button>
          
          <div style={{ color: 'var(--text-muted)', fontSize: '14px' }}>
            Agent Orchestrator
          </div>
          
          <ChevronDown size={16} style={{ color: 'var(--text-muted)' }} />
          
          <select
            value={selectedAgent?.id}
            onChange={(e) => {
              const agent = fullAgentSuite.find(a => a.id === e.target.value);
              setSelectedAgent(agent || null);
              navigate(`/orchestrator/${e.target.value}`);
            }}
            style={{
              padding: '8px 12px',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '600',
              minWidth: '300px',
              background: 'var(--card-bg)'
            }}
          >
            {fullAgentSuite.map(agent => (
              <option key={agent.id} value={agent.id}>
                {agent.name} ({agent.provider === 'hosted' ? 'Hosted' : 'Native'})
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedAgent && (
        <div style={{ display: 'flex', gap: '24px', height: 'calc(100% - 120px)' }}>
          {/* Main Content Area */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Enhanced Agent Header Card */}
            <div className="sn-panel">
              <div className="sn-panel-body">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ display: 'flex', gap: '24px' }}>
                    <div style={{
                      width: '72px',
                      height: '72px',
                      borderRadius: '12px',
                      background: `linear-gradient(135deg, ${providerInfo.color} 0%, ${providerInfo.color}80 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      boxShadow: `0 4px 16px ${providerInfo.color}40`
                    }}>
                      {providerInfo.icon}
                    </div>
                    
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '6px' }}>
                        <h2 style={{ fontSize: '24px', margin: 0 }}>{selectedAgent.name}</h2>
                        <div className={`provider-badge ${selectedAgent.provider}`}>
                          {selectedAgent.provider === 'hosted' ? 'HOSTED' : 'NATIVE'}
                        </div>
                      </div>
                      <div style={{ color: providerInfo.color, fontSize: '14px', fontWeight: '500', marginBottom: '8px' }}>
                        {providerInfo.label}
                      </div>
                      <p style={{ color: 'var(--text-secondary)', marginBottom: '12px', maxWidth: '500px' }}>
                        {selectedAgent.description}
                      </p>
                      <div style={{ display: 'flex', gap: '20px', fontSize: '13px' }}>
                        <span>
                          <strong>Version:</strong> {selectedAgent.provenance?.version || agentConfig.version}
                        </span>
                        <span>
                          <strong>Status:</strong>{' '}
                          <span style={{ color: isAgentPaused ? 'var(--status-warning)' : 'var(--status-active)' }}>
                            {isAgentPaused ? 'Paused' : 'Active'}
                          </span>
                        </span>
                        <span>
                          <strong>Uptime:</strong> 99.{selectedAgent.provider === 'hosted' ? '4' : '8'}%
                        </span>
                        {selectedAgent.provider === 'hosted' && (
                          <span>
                            <strong>Integration:</strong>{' '}
                            <span style={{ color: platformStatus.hostedAgentHealth[selectedAgent.id as keyof typeof platformStatus.hostedAgentHealth] === 'operational' ? 'var(--status-active)' : 'var(--status-warning)' }}>
                              {platformStatus.hostedAgentHealth[selectedAgent.id as keyof typeof platformStatus.hostedAgentHealth] || 'operational'}
                            </span>
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      onClick={() => setIsAgentPaused(!isAgentPaused)}
                      className={`sn-button ${isAgentPaused ? 'sn-button-primary' : ''}`}
                    >
                      {isAgentPaused ? (
                        <>
                          <Play size={14} />
                          Resume
                        </>
                      ) : (
                        <>
                          <Pause size={14} />
                          Pause
                        </>
                      )}
                    </button>
                    <button className="sn-button">
                      <Settings size={14} />
                      Configure
                    </button>
                    <button className="sn-button">
                      <Download size={14} />
                      Export Logs
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Enhanced Key Metrics Row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px' }}>
              <div className="metric-card">
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', textTransform: 'uppercase' }}>
                  {selectedAgent.provider === 'hosted' ? 'External Processing' : 'Last Hour'}
                </div>
                <div className="metric-value">
                  {metrics.lastHour.processed}
                </div>
                <div style={{ fontSize: '11px', color: metrics.lastHour.failed > 0 ? 'var(--status-error)' : 'var(--status-active)' }}>
                  {metrics.lastHour.failed} failed
                </div>
              </div>
              
              <div className="metric-card">
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', textTransform: 'uppercase' }}>
                  {selectedAgent.provider === 'hosted' ? 'Integration Success' : 'Success Rate'}
                </div>
                <div className="metric-value" style={{ color: 'var(--status-active)' }}>
                  {selectedAgent.performance.successRate}%
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                  Last 24 hours
                </div>
              </div>
              
              <div className="metric-card">
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', textTransform: 'uppercase' }}>
                  Avg Processing
                </div>
                <div className="metric-value">
                  {selectedAgent.performance.avgProcessingTime}s
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                  {selectedAgent.provider === 'hosted' ? 'Cross-platform' : 'Per document'}
                </div>
              </div>
              
              <div className="metric-card">
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', textTransform: 'uppercase' }}>
                  Queue Depth
                </div>
                <div className="metric-value" style={{ color: metrics.lastHour.queueDepth > 10 ? 'var(--status-warning)' : 'var(--text-primary)' }}>
                  {metrics.lastHour.queueDepth}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                  {metrics.lastHour.avgWaitTime}s avg wait
                </div>
              </div>
            </div>

            {/* Enhanced Tabbed Content */}
            <div className="sn-panel" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              <div style={{ 
                display: 'flex', 
                borderBottom: '1px solid var(--border-color)',
                background: 'var(--divider-light)'
              }}>
                <button
                  onClick={() => setActiveTab('performance')}
                  style={{
                    padding: '14px 24px',
                    border: 'none',
                    background: activeTab === 'performance' ? 'var(--card-bg)' : 'transparent',
                    borderBottom: activeTab === 'performance' ? `2px solid ${providerInfo.color}` : 'none',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: activeTab === 'performance' ? '600' : '400',
                    color: activeTab === 'performance' ? providerInfo.color : 'var(--text-secondary)'
                  }}
                >
                  Performance & Health
                </button>
                <button
                  onClick={() => setActiveTab('configuration')}
                  style={{
                    padding: '14px 24px',
                    border: 'none',
                    background: activeTab === 'configuration' ? 'var(--card-bg)' : 'transparent',
                    borderBottom: activeTab === 'configuration' ? `2px solid ${providerInfo.color}` : 'none',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: activeTab === 'configuration' ? '600' : '400',
                    color: activeTab === 'configuration' ? providerInfo.color : 'var(--text-secondary)'
                  }}
                >
                  Configuration
                </button>
                <button
                  onClick={() => setActiveTab('integration')}
                  style={{
                    padding: '14px 24px',
                    border: 'none',
                    background: activeTab === 'integration' ? 'var(--card-bg)' : 'transparent',
                    borderBottom: activeTab === 'integration' ? `2px solid ${providerInfo.color}` : 'none',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: activeTab === 'integration' ? '600' : '400',
                    color: activeTab === 'integration' ? providerInfo.color : 'var(--text-secondary)'
                  }}
                >
                  {selectedAgent.provider === 'hosted' ? 'Integration Status' : 'Agent Dependencies'}
                </button>
              </div>
              
              <div className="sn-panel-body" style={{ flex: 1, overflowY: 'auto' }}>
                {activeTab === 'performance' && (
                  <div>
                    {/* Performance Graphs */}
                    <div style={{ marginBottom: '32px' }}>
                      <h4 style={{ marginBottom: '20px', color: 'var(--text-primary)' }}>
                        Real-time Performance
                      </h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
                        <div className="metric-card" style={{ padding: '20px' }}>
                          <div style={{ marginBottom: '16px', fontSize: '14px', fontWeight: '600' }}>
                            <strong>CPU Usage</strong>
                            <span style={{ float: 'right', color: providerInfo.color, fontSize: '16px' }}>{metrics.performance.cpuUsage}%</span>
                          </div>
                          <div className="progress-bar">
                            <div 
                              className={`progress-fill ${metrics.performance.cpuUsage > 80 ? 'warning' : 'success'}`}
                              style={{
                                width: `${metrics.performance.cpuUsage}%`,
                                background: metrics.performance.cpuUsage > 80 ? 'var(--status-error)' : providerInfo.color,
                              }}
                            />
                          </div>
                        </div>
                        
                        <div className="metric-card" style={{ padding: '20px' }}>
                          <div style={{ marginBottom: '16px', fontSize: '14px', fontWeight: '600' }}>
                            <strong>{selectedAgent.provider === 'hosted' ? 'Integration Latency' : 'Memory Usage'}</strong>
                            <span style={{ float: 'right', color: providerInfo.color, fontSize: '16px' }}>
                              {selectedAgent.provider === 'hosted' ? `${metrics.performance.apiLatency}ms` : `${metrics.performance.memoryUsage}%`}
                            </span>
                          </div>
                          <div className="progress-bar">
                            <div 
                              className="progress-fill"
                              style={{
                                width: selectedAgent.provider === 'hosted' 
                                  ? `${Math.min(metrics.performance.apiLatency / 5, 100)}%` 
                                  : `${metrics.performance.memoryUsage}%`,
                                background: selectedAgent.provider === 'hosted'
                                  ? (metrics.performance.apiLatency > 400 ? 'var(--status-warning)' : providerInfo.color)
                                  : (metrics.performance.memoryUsage > 80 ? 'var(--status-error)' : 'var(--status-warning)'),
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Capabilities */}
                    <div style={{ marginBottom: '32px' }}>
                      <h4 style={{ marginBottom: '20px', color: 'var(--text-primary)' }}>
                        Agent Capabilities
                      </h4>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                        {selectedAgent.capabilities.map((capability, idx) => (
                          <div
                            key={idx}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '8px 16px',
                              background: `${providerInfo.color}10`,
                              border: `1px solid ${providerInfo.color}30`,
                              borderRadius: '20px',
                              fontSize: '13px',
                              color: providerInfo.color,
                              fontWeight: '500'
                            }}
                          >
                            {getCapabilityIcon(capability)}
                            {capability}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Provider-specific benefits */}
                    {providerInfo.benefits && (
                      <div>
                        <h4 style={{ marginBottom: '20px', color: 'var(--text-primary)' }}>
                          {selectedAgent.provider === 'hosted' ? 'Integration Benefits' : 'Platform Benefits'}
                        </h4>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                          {providerInfo.benefits.map((benefit, idx) => (
                            <div key={idx} className="metric-card" style={{ padding: '16px', textAlign: 'center' }}>
                              <div style={{ color: providerInfo.color, marginBottom: '8px' }}>
                                <CheckCircle size={24} />
                              </div>
                              <div style={{ fontSize: '12px', fontWeight: '500' }}>
                                {benefit}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'configuration' && (
                  <div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
                      {/* Configuration Rules */}
                      <div>
                        <h4 style={{ marginBottom: '20px', color: 'var(--text-primary)' }}>
                          {selectedAgent.provider === 'hosted' ? 'Integration Configuration' : 'Validation Rules'}
                        </h4>
                        <div style={{ 
                          padding: '20px', 
                          background: 'var(--primary-bg)', 
                          borderRadius: '8px',
                          fontFamily: 'monospace',
                          fontSize: '12px',
                          color: 'var(--status-active)',
                          border: '1px solid var(--border-dark)'
                        }}>
                          <pre>{JSON.stringify(agentConfig.validationRules, null, 2)}</pre>
                        </div>
                      </div>

                      {/* ML Models / Integration Details */}
                      <div>
                        <h4 style={{ marginBottom: '20px', color: 'var(--text-primary)' }}>
                          {selectedAgent.provider === 'hosted' ? 'External Models' : 'ML Models'}
                        </h4>
                        <div style={{ fontSize: '13px' }}>
                          {Object.entries(agentConfig.mlModels).map(([key, value]) => (
                            <div key={key} className="metric-card" style={{ 
                              padding: '16px', 
                              marginBottom: '12px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}>
                              <span style={{ fontWeight: '600', textTransform: 'capitalize' }}>
                                {key.replace(/_/g, ' ')}:
                              </span>
                              <span style={{ 
                                fontFamily: 'monospace', 
                                color: providerInfo.color,
                                fontSize: '12px'
                              }}>
                                {value}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Processing Thresholds */}
                    <div style={{ marginTop: '32px' }}>
                      <h4 style={{ marginBottom: '20px', color: 'var(--text-primary)' }}>
                        Processing Thresholds
                      </h4>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px' }}>
                        {Object.entries(agentConfig.thresholds).map(([key, value]) => (
                          <div key={key} className="metric-card" style={{ textAlign: 'center', padding: '20px' }}>
                            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                              {key.replace(/_/g, ' ')}
                            </div>
                            <div className="metric-value" style={{ color: providerInfo.color }}>
                              {value}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'integration' && (
                  <div>
                    {selectedAgent.provider === 'hosted' ? (
                      // Hosted Agent Integration Status
                      <div>
                        <div style={{ marginBottom: '32px' }}>
                          <h4 style={{ marginBottom: '20px', color: 'var(--text-primary)' }}>
                            External Integration Status
                          </h4>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
                            <div className="metric-card" style={{ padding: '20px' }}>
                              <h5 style={{ 
                                color: 'var(--provider-hosted)', 
                                marginBottom: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}>
                                <Shield size={16} />
                                Platform Status
                              </h5>
                              <div style={{ fontSize: '12px' }}>
                                <div style={{ marginBottom: '8px' }}>
                                  <strong>Cross-platform Context:</strong> {platformStatus.crossPlatformContext}
                                </div>
                                <div style={{ marginBottom: '8px' }}>
                                  <strong>Multi-vendor Coordination:</strong> {platformStatus.multiVendorCoordination}
                                </div>
                                <div>
                                  <strong>Version Compatibility:</strong> {platformStatus.versionCompatibility}
                                </div>
                              </div>
                            </div>

                            <div className="metric-card" style={{ padding: '20px' }}>
                              <h5 style={{ 
                                color: 'var(--provider-hosted)', 
                                marginBottom: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}>
                                <GitBranch size={16} />
                                AWS Agent Code
                              </h5>
                              <div style={{ fontSize: '12px' }}>
                                <div style={{ marginBottom: '8px' }}>
                                  <strong>Vendor:</strong> {selectedAgent.provenance?.vendor || 'External Provider'}
                                </div>
                                <div style={{ marginBottom: '8px' }}>
                                  <strong>Integration:</strong> {selectedAgent.provenance?.integration || 'conductor'}
                                </div>
                                <div>
                                  <strong>Deployment:</strong> {selectedAgent.provenance?.deploymentType || 'hosted'}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // Native Agent Dependencies
                      <div>
                        <h4 style={{ marginBottom: '20px', color: 'var(--text-primary)' }}>
                          Agent Dependencies
                        </h4>
                        {dependencies && (
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                            {/* Tools */}
                            <div>
                              <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '8px', 
                                marginBottom: '16px',
                                fontSize: '16px',
                                fontWeight: '600',
                                color: 'var(--provider-native)'
                              }}>
                                <Wrench size={20} />
                                Connected Tools
                              </div>
                              <div style={{ fontSize: '12px' }}>
                                {dependencies.tools.map((tool, idx) => (
                                  <div
                                    key={idx}
                                    className="metric-card"
                                    style={{
                                      padding: '12px 16px',
                                      marginBottom: '8px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '12px'
                                    }}
                                  >
                                    <div style={{
                                      width: '8px',
                                      height: '8px',
                                      borderRadius: '50%',
                                      background: 'var(--status-active)'
                                    }} />
                                    <span style={{ fontWeight: '500' }}>{tool}</span>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* RAG Assets */}
                            <div>
                              <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '8px', 
                                marginBottom: '16px',
                                fontSize: '16px',
                                fontWeight: '600',
                                color: 'var(--status-warning)'
                              }}>
                                <BookOpen size={20} />
                                Knowledge Assets (RAG)
                              </div>
                              <div style={{ fontSize: '12px' }}>
                                {dependencies.ragAssets.map((asset, idx) => (
                                  <div
                                    key={idx}
                                    className="metric-card"
                                    style={{
                                      padding: '12px 16px',
                                      marginBottom: '8px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '12px'
                                    }}
                                  >
                                    <div style={{
                                      width: '8px',
                                      height: '8px',
                                      borderRadius: '50%',
                                      background: 'var(--status-warning)'
                                    }} />
                                    <span style={{ fontWeight: '500' }}>{asset}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Panel - Live Activity */}
          <div style={{ width: '380px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Current Processing */}
            <div className="sn-panel">
              <div className="sn-panel-header">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Live Processing</span>
                  <Activity size={16} className="agent-active" style={{ color: providerInfo.color }} />
                </div>
              </div>
              <div className="sn-panel-body">
                {currentTasks.filter(t => t.status === 'processing').map(task => (
                  <div key={task.id} className="metric-card" style={{ 
                    marginBottom: '16px',
                    padding: '16px',
                    background: `${providerInfo.color}08`,
                    border: `1px solid ${providerInfo.color}20`
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                      <strong style={{ fontSize: '13px', color: 'var(--text-primary)' }}>Task {task.id}</strong>
                      <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                        {task.startTime ? `${Math.floor((Date.now() - task.startTime.getTime()) / 1000)}s ago` : ''}
                      </span>
                    </div>
                    <div style={{ fontSize: '12px', marginBottom: '10px', color: 'var(--text-secondary)' }}>
                      {task.documentName}
                    </div>
                    <div style={{ marginBottom: '10px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '6px' }}>
                        <span>Progress</span>
                        <span style={{ color: providerInfo.color, fontWeight: '600' }}>{task.progress}%</span>
                      </div>
                      <div className="progress-bar">
                        <div 
                          className="progress-fill processing"
                          style={{
                            width: `${task.progress}%`,
                            background: providerInfo.color,
                          }} 
                        />
                      </div>
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                      Status: {task.currentStep}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Task Queue */}
            <div className="sn-panel" style={{ flex: 1 }}>
              <div className="sn-panel-header">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Task Queue</span>
                  <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                    {currentTasks.filter(t => t.status === 'queued').length} waiting
                  </span>
                </div>
              </div>
              <div className="sn-panel-body" style={{ overflowY: 'auto' }}>
                <div style={{ fontSize: '12px' }}>
                  {currentTasks.filter(t => t.status === 'queued').map((task, idx) => (
                    <div key={task.id} style={{ 
                      padding: '12px 16px', 
                      borderBottom: idx < currentTasks.filter(t => t.status === 'queued').length - 1 ? '1px solid var(--border-color)' : 'none',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div>
                        <div style={{ fontWeight: '600', marginBottom: '4px' }}>{task.documentName}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                          Workflow: {task.workflowId}
                        </div>
                      </div>
                      <div className={`provider-badge ${task.priority === 'high' ? 'native' : 'hosted'}`} style={{
                        fontSize: '9px',
                        padding: '2px 6px',
                      }}>
                        {task.priority.toUpperCase()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Enhanced Alerts */}
            <div className="sn-panel">
              <div className="sn-panel-header">Active Alerts</div>
              <div className="sn-panel-body">
                <div style={{ fontSize: '12px' }}>
                  {selectedAgent.provider === 'hosted' ? (
                    <>
                      <div style={{ 
                        padding: '12px', 
                        background: '#e3f2fd', 
                        borderRadius: '6px',
                        marginBottom: '10px',
                        display: 'flex',
                        gap: '10px',
                        border: '1px solid var(--provider-hosted)30'
                      }}>
                        <RefreshCw size={16} color="var(--provider-hosted)" />
                        <div>
                          <div style={{ fontWeight: '600' }}>Integration stable</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                            AWS Agent Code connectivity: 99.4%
                          </div>
                        </div>
                      </div>
                      
                      <div style={{ 
                        padding: '12px', 
                        background: '#fff3e0', 
                        borderRadius: '6px',
                        display: 'flex',
                        gap: '10px',
                        border: '1px solid var(--status-warning)30'
                      }}>
                        <AlertCircle size={16} color="var(--status-warning)" />
                        <div>
                          <div style={{ fontWeight: '600' }}>Latency increase</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                            Cross-platform response time: +15%
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ 
                        padding: '12px', 
                        background: '#fff3e0', 
                        borderRadius: '6px',
                        marginBottom: '10px',
                        display: 'flex',
                        gap: '10px',
                        border: '1px solid var(--status-warning)30'
                      }}>
                        <AlertCircle size={16} color="var(--status-warning)" />
                        <div>
                          <div style={{ fontWeight: '600' }}>High retry rate detected</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                            12 corrupted PDFs in last hour
                          </div>
                        </div>
                      </div>
                      
                      <div style={{ 
                        padding: '12px', 
                        background: '#e8f5e9', 
                        borderRadius: '6px',
                        display: 'flex',
                        gap: '10px',
                        border: '1px solid var(--status-active)30'
                      }}>
                        <CheckCircle size={16} color="var(--status-active)" />
                        <div>
                          <div style={{ fontWeight: '600' }}>Model update available</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                            insurance-ocr-v3.2 → v3.3
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};